import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, CheckCircle2, Loader2, BellRing, X,
} from "lucide-react";
import { Broadcasts } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { playNotification } from "@/lib/sfx";

/**
 * BroadcastGate — global always-mounted component that pings the backend
 * for admin broadcasts targeted at the logged-in user and pops a modal for
 * each one until the user explicitly clicks "Potwierdzam odczyt".
 *
 * Behaviour:
 *   • Polls `/api/broadcasts/pending` every 30s (light) and immediately on
 *     window focus (so users see fresh broadcasts the moment they alt-tab
 *     back to the app).
 *   • Plays a `playNotification()` chime the first time a NEW broadcast id
 *     appears in the poll response.
 *   • Requests browser notification permission on the first broadcast and,
 *     if granted, spawns a native OS notification so the user notices even
 *     when the tab is unfocused / minimized.
 *   • Modal blocks the app until user acks. If multiple pending broadcasts
 *     exist, they're shown one-by-one (oldest first) so context stays clean.
 */
const POLL_INTERVAL_MS = 30000;

export default function BroadcastGate() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]); // pending broadcasts (oldest first)
  const [busy, setBusy] = useState(false);
  const seenIdsRef = useRef(new Set()); // ids we've already alerted on
  const acknowledgedIdsRef = useRef(new Set());
  const activeUserRef = useRef(null);
  const pollTimerRef = useRef(null);

  const poll = async () => {
    if (!user) return;
    try {
      const { pending } = await Broadcasts.pending();
      if (activeUserRef.current !== user.user_id) return;
      // Oldest-first so users work through the queue chronologically.
      const sorted = [...(pending || [])].filter((b) => !acknowledgedIdsRef.current.has(b.id)).sort((a, b) =>
        (a.created_at || "").localeCompare(b.created_at || ""),
      );
      // Detect NEW broadcasts to play sound + native notification.
      const seen = seenIdsRef.current;
      const newOnes = sorted.filter((b) => !seen.has(b.id));
      if (newOnes.length > 0) {
        // First-load quiet path: don't chime on initial fetch if a broadcast
        // was pending from before this session — only for genuinely new
        // ones observed after the initial poll.
        if (seen.size > 0) {
          try { playNotification(); } catch (_) { /* audio blocked */ }
          for (const b of newOnes) {
            spawnNativeNotification(b);
          }
        } else {
          // On initial poll, still ask for permission preemptively so future
          // broadcasts CAN trigger the OS notification.
          maybeRequestPermission();
        }
        newOnes.forEach((b) => seen.add(b.id));
      }
      setQueue(sorted);
    } catch (_) {
      // Silent — polling failures aren't user-facing.
    }
  };

  // Start polling loop + focus-triggered refresh.
  useEffect(() => {
    activeUserRef.current = user?.user_id || null;
    acknowledgedIdsRef.current = new Set();
    seenIdsRef.current = new Set();
    setQueue([]);
    if (!user) {
      setQueue([]);
      seenIdsRef.current = new Set();
      return;
    }
    poll();
    pollTimerRef.current = window.setInterval(poll, POLL_INTERVAL_MS);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => {
      activeUserRef.current = null;
      if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || queue.length === 0) return null;
  const active = queue[0]; // show one at a time, oldest first

  const acceptAndAdvance = async () => {
    setBusy(true);
    try {
      await Broadcasts.ack(active.id);
      acknowledgedIdsRef.current.add(active.id);
      // Optimistically remove from queue
      setQueue((prev) => prev.filter((b) => b.id !== active.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd potwierdzania");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1600] bg-zinc-950/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6"
      data-testid="broadcast-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg max-h-[92vh] rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-300">
        {/* Header */}
        <div className="relative overflow-hidden px-5 py-5 bg-gradient-to-br from-orange-500 via-red-600 to-zinc-950 text-white">
          <Megaphone className="absolute -right-6 -bottom-8 w-40 h-40 opacity-15" strokeWidth={1.5} />
          <div className="relative flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center ring-1 ring-white/25 shrink-0">
              <BellRing className="w-5 h-5 animate-[flash-good_1.4s_ease-out_infinite]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
                // Komunikat od {active.created_by_name || "administratora"}
              </div>
              <h2
                data-testid="broadcast-title"
                className="font-heading font-black uppercase tracking-tight text-lg sm:text-2xl mt-1"
              >
                {active.title}
              </h2>
              <div className="text-[10px] font-mono text-white/60 mt-1">
                {new Date(active.created_at).toLocaleString("pl-PL")}
                {queue.length > 1 && ` · ${queue.length - 1} kolejnych w kolejce`}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {active.image_base64 && (
            <img
              data-testid="broadcast-image"
              src={active.image_base64}
              alt="Ilustracja komunikatu"
              className="w-full rounded-2xl ring-1 ring-zinc-200"
            />
          )}
          {active.body && (
            <p
              data-testid="broadcast-body"
              className="text-sm leading-relaxed text-zinc-700 whitespace-pre-wrap break-words"
            >
              {active.body}
            </p>
          )}
          {!active.body && !active.image_base64 && (
            <p className="text-sm text-zinc-400 italic">Brak dodatkowej treści.</p>
          )}
        </div>

        {/* Footer — MUST click to close */}
        <div className="p-4 sm:p-5 border-t border-zinc-100 bg-zinc-50 flex items-center gap-3">
          <div className="flex-1 text-[11px] font-mono text-zinc-500">
            // Kliknij poniżej aby potwierdzić że przeczytałeś/aś.
          </div>
          <button
            onClick={acceptAndAdvance}
            disabled={busy}
            data-testid="broadcast-ack-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-heading font-black uppercase tracking-widest text-sm bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg active:scale-95 transition-all disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {busy ? "Zapisuję…" : "Potwierdzam odczyt"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Native OS notification helpers ──────────────────────────────────────
async function maybeRequestPermission() {
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "granted" || Notification.permission === "denied") return;
  try { await Notification.requestPermission(); } catch (_) { /* ignore */ }
}

function spawnNativeNotification(bc) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(bc.title || "Nowy komunikat", {
      body: (bc.body || "").slice(0, 160) || "Otwórz aplikację, aby zobaczyć szczegóły.",
      icon: bc.image_base64 || "/favicon.ico",
      tag: `bc-${bc.id}`, // dedupe if OS re-fires
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    // Auto-close after 8s so the tray doesn't fill up.
    window.setTimeout(() => { try { n.close(); } catch (_) { /* ignore */ } }, 8000);
  } catch (_) {
    // Some browsers throw when tab is inactive — safe to swallow.
  }
}
